// Frontend/src/components/rates/BCVRateHistory.tsx
//
// The official rate, day by day, and how each day's figure was arrived at.
//
// It lists every CALENDAR day, not every published rate, and that is the
// whole design. BCV publishes on banking days only — no weekends, no public
// holidays — so a table of published rows has a hole exactly where somebody
// is asking "what rate did you bill me at on the 24th?". Each day therefore
// says either "published" or "carried forward from <date>".
//
// One honesty rule runs through it: a Saturday is provably a weekend, but a
// Wednesday with no publication is NOT provably a holiday — a broken scraper
// looks identical. So that case reads "no publication", and a run of them is
// surfaced as a warning rather than dressed up as a holiday.

import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Minus,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getBCVRateHistory, todayInCaracas, type BCVRateDay } from "@/api/payments";

const BRAND = "#1F3A93";

/** Tones reuse the issue palette's logic: published is calm, gaps recede. */
const PUBLISHED = { bg: "#E7EBF8", fg: "#1B3480", ring: "#C3CDEC" };
const WEEKEND = { bg: "#F1F5F9", fg: "#475569", ring: "#E2E8F0" };
const NO_PUB = { bg: "#FEF3C7", fg: "#92400E", ring: "#FDE0A3" };
const UNCOVERED = { bg: "#FFFFFF", fg: "#9CA3AF", ring: "#E5E7EB" };

function currentMonth(): string {
  return todayInCaracas().slice(0, 7);
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export default function BCVRateHistory({
  /** Scroll to and highlight this day — used when linking from a payment. */
  highlightDate,
}: {
  highlightDate?: string | null;
}) {
  const { t, language } = useLanguage();
  const locale = language === "es" ? "es-VE" : "en-US";

  const [month, setMonth] = React.useState(
    () => highlightDate?.slice(0, 7) || currentMonth()
  );

  const history = useQuery({
    queryKey: ["bcv-rate-history", month],
    queryFn: () => getBCVRateHistory(month),
    // A past month's rates are settled facts; only the current month moves.
    staleTime: month === currentMonth() ? 60_000 : Infinity,
    placeholderData: (previous) => previous,
  });

  const rows = history.data?.days ?? [];
  const summary = history.data?.summary;
  const atCurrentMonth = month >= currentMonth();

  // A month entirely before our first rate has nothing to show. Rendering it
  // as thirty-one rows of em-dashes is technically accurate and useless --
  // and it is what the first version did.
  const covered = rows.some((d) => d.rate !== null);
  const coverageMonth = summary?.coverage_starts?.slice(0, 7);
  // Nothing before the first rate we hold is worth navigating to.
  const atEarliestMonth = !!coverageMonth && month <= coverageMonth;

  const monthLabel = new Date(`${month}-01T12:00:00`).toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="space-y-4">
      {/* ---- Month navigation ---- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <NavButton
            onClick={() => setMonth(shiftMonth(month, -1))}
            disabled={atEarliestMonth}
            label={t("rates.previousMonth")}
          >
            <ChevronLeft className="h-4 w-4" />
          </NavButton>
          <span className="min-w-[9rem] text-center text-[15px] font-semibold capitalize text-gray-900">
            {monthLabel}
          </span>
          <NavButton
            onClick={() => setMonth(shiftMonth(month, 1))}
            disabled={atCurrentMonth}
            label={t("rates.nextMonth")}
          >
            <ChevronRight className="h-4 w-4" />
          </NavButton>
        </div>

        <input
          type="month"
          value={month}
          max={currentMonth()}
          onChange={(e) => e.target.value && setMonth(e.target.value)}
          aria-label={t("rates.jumpToMonth")}
          className="h-9 rounded-xl bg-white px-3 text-sm text-gray-700 ring-1 ring-gray-200 focus:outline-none focus:ring-2 focus:ring-[#1F3A93]"
        />
      </div>

      {/* ---- Coverage ---- */}
      {summary && covered && (
        <>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <Tile label={t("rates.published")} value={summary.published} tone={PUBLISHED} />
            <Tile label={t("rates.carried")} value={summary.carried} tone={WEEKEND} />
            <Tile
              label={t("rates.longestGap")}
              value={summary.longest_weekday_gap}
              tone={summary.longest_weekday_gap > 4 ? NO_PUB : WEEKEND}
            />
            <Tile
              label={t("rates.uncovered")}
              value={summary.uncovered}
              tone={summary.uncovered ? NO_PUB : WEEKEND}
            />
          </div>

          {/* A long run of unpublished weekdays is an outage, not a holiday. */}
          {summary.longest_weekday_gap > 4 && (
            <Banner tone="warn">
              {t("rates.gapWarning", { days: summary.longest_weekday_gap })}
            </Banner>
          )}
          {summary.today_is_stale && (
            <Banner tone="warn">
              {t("rates.staleWarning", { days: summary.today_days_carried ?? 0 })}
            </Banner>
          )}
        </>
      )}

      {/* ---- Table ---- */}
      <div className="overflow-hidden rounded-3xl bg-white/80 ring-1 ring-black/5 backdrop-blur-xl">
        {history.isLoading ? (
          <TableSkeleton />
        ) : history.isError ? (
          <div className="px-4 py-10 text-center text-sm text-red-700">
            {t("rates.loadError")}
          </div>
        ) : !covered ? (
          <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-gray-100 text-gray-400">
              <CalendarDays className="h-5 w-5" aria-hidden />
            </span>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                {t("rates.emptyMonth")}
              </p>
              <p className="mx-auto mt-1 max-w-xs text-xs text-gray-500">
                {summary?.coverage_starts
                  ? t("rates.coverageStarts", {
                      date: new Date(`${summary.coverage_starts}T12:00:00`).toLocaleDateString(
                        locale,
                        { day: "numeric", month: "long", year: "numeric" }
                      ),
                    })
                  : t("rates.noDataYet")}
              </p>
            </div>
            {summary?.coverage_starts && (
              <button
                type="button"
                onClick={() => setMonth(summary.coverage_starts!.slice(0, 7))}
                className="rounded-xl bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50"
              >
                {t("rates.jumpToFirst")}
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Header only on a wide screen; the rows carry their own labels
                on a phone, where a five-column header does not fit. */}
            <div className="hidden grid-cols-[7rem_1fr_7rem_9rem] gap-3 border-b border-black/5 px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500 sm:grid">
              <span>{t("rates.day")}</span>
              <span>{t("rates.source")}</span>
              <span className="text-right">{t("rates.change")}</span>
              <span className="text-right">{t("rates.rate")}</span>
            </div>

            <ul className="divide-y divide-black/5">
              {rows.map((day) => (
                <DayRow
                  key={day.date}
                  day={day}
                  locale={locale}
                  highlighted={day.date === highlightDate}
                />
              ))}
            </ul>
          </>
        )}
      </div>

      <p className="px-1 text-[11px] leading-relaxed text-gray-500">
        {t("rates.footnote")}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
function DayRow({
  day,
  locale,
  highlighted,
}: {
  day: BCVRateDay;
  locale: string;
  highlighted?: boolean;
}) {
  const { t } = useLanguage();
  const ref = React.useRef<HTMLLIElement>(null);

  React.useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [highlighted]);

  const tone = day.reason === null
    ? PUBLISHED
    : day.reason === "WEEKEND"
    ? WEEKEND
    : day.reason === "NO_PUBLICATION"
    ? NO_PUB
    : UNCOVERED;

  const date = new Date(`${day.date}T12:00:00`);
  const change = day.change ? Number(day.change) : 0;
  const ChangeIcon = change > 0 ? TrendingUp : change < 0 ? TrendingDown : Minus;

  const label =
    day.reason === null
      ? t("rates.reason.published")
      : day.reason === "WEEKEND"
      ? t("rates.reason.weekend")
      : day.reason === "NO_PUBLICATION"
      ? t("rates.reason.noPublication")
      : t("rates.reason.beforeCoverage");

  return (
    <li
      ref={ref}
      className={`grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-1 px-4 py-2.5 transition sm:grid-cols-[7rem_1fr_7rem_9rem] ${
        highlighted ? "bg-[#E7EBF8]" : day.reason ? "bg-transparent" : "bg-white/40"
      }`}
    >
      {/* Day */}
      <div className="flex items-baseline gap-1.5 sm:block">
        <span className="text-sm font-semibold tabular-nums text-gray-900">
          {date.toLocaleDateString(locale, { day: "2-digit", month: "short" })}
        </span>
        <span className="text-[11px] capitalize text-gray-500">
          {date.toLocaleDateString(locale, { weekday: "short" })}
        </span>
      </div>

      {/* Rate — second on a phone so the number sits beside the date */}
      <div className="text-right sm:order-4">
        {day.rate ? (
          <span className="text-sm font-semibold tabular-nums text-gray-900">
            {Number(day.rate).toLocaleString(locale, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            })}
          </span>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </div>

      {/* Source */}
      <div className="col-span-2 flex flex-wrap items-center gap-1.5 sm:col-span-1 sm:order-2">
        <span
          className="inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
          style={{ background: tone.bg, color: tone.fg, boxShadow: `inset 0 0 0 1px ${tone.ring}` }}
        >
          {label}
        </span>
        {day.carried_from && (
          <span className="text-[11px] text-gray-500">
            {t("rates.carriedFrom", {
              date: new Date(`${day.carried_from}T12:00:00`).toLocaleDateString(locale, {
                day: "2-digit",
                month: "short",
              }),
              days: day.days_carried ?? 0,
            })}
          </span>
        )}
      </div>

      {/* Change */}
      <div className="col-span-2 text-right sm:col-span-1 sm:order-3">
        {day.change !== null && change !== 0 ? (
          <span
            className={`inline-flex items-center justify-end gap-1 text-[11px] font-medium tabular-nums ${
              change > 0 ? "text-red-700" : "text-emerald-700"
            }`}
          >
            <ChangeIcon className="h-3 w-3" aria-hidden />
            {change > 0 ? "+" : ""}
            {day.change_pct}%
          </span>
        ) : (
          <span className="text-[11px] text-gray-300 sm:inline">—</span>
        )}
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ */
function Tile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: { bg: string; fg: string; ring: string };
}) {
  return (
    <div
      className="rounded-2xl px-3.5 py-3"
      style={{ background: tone.bg, color: tone.fg, boxShadow: `inset 0 0 0 1px ${tone.ring}` }}
    >
      <div className="text-[11px] font-medium">{label}</div>
      <div className="mt-0.5 text-2xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function Banner({ tone, children }: { tone: "warn"; children: React.ReactNode }) {
  return (
    <p
      className="flex items-start gap-2 rounded-2xl px-3.5 py-2.5 text-sm"
      style={{
        background: NO_PUB.bg,
        color: NO_PUB.fg,
        boxShadow: `inset 0 0 0 1px ${NO_PUB.ring}`,
      }}
      role={tone === "warn" ? "status" : undefined}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

function NavButton({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="grid h-9 w-9 place-items-center rounded-full bg-white text-gray-600 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
      style={{ outlineColor: BRAND }}
    >
      {children}
    </button>
  );
}

function TableSkeleton() {
  return (
    <ul className="divide-y divide-black/5" aria-hidden>
      {Array.from({ length: 8 }).map((_, i) => (
        <li key={i} className="flex animate-pulse items-center gap-3 px-4 py-3">
          <CalendarDays className="h-4 w-4 shrink-0 text-gray-200" />
          <span className="h-3 w-16 rounded bg-gray-200" />
          <span className="h-3 w-24 rounded bg-gray-100" />
          <span className="ml-auto h-3 w-20 rounded bg-gray-200" />
        </li>
      ))}
    </ul>
  );
}
